<!-- Costs: brokerage commissions, fees and the hidden cost of funds (TER). -->
<script lang="ts">
  import * as X from "../core/analysis";
  import { app, need } from "../lib/state.svelte";
  import { saveConfig, toast } from "../lib/actions";
  import { parseNum } from "../lib/format";
  import { baseOption, compactMoney, palette, series, tipMoney, tooltipRows, valueAxis, type TipParam } from "../lib/chart";
  import ViewHead from "../components/ViewHead.svelte";
  import Kpi from "../components/Kpi.svelte";
  import Card from "../components/Card.svelte";
  import Chart from "../components/Chart.svelte";
  import Money from "../components/Money.svelte";
  import Pct from "../components/Pct.svelte";

  interface FeeYear { comm: number; other: number; byBroker: Record<string, number> }

  const m = $derived(need(app.model));
  const cfg = $derived(need(app.data).config);
  const terMap = $derived(cfg.ter || {});
  const tc = $derived(X.terCosts(m, app.accList, terMap));
  const fees = $derived.by(() => {
    const S = new Set(app.accList);
    const feeByYear: Record<string, FeeYear> = {};
    const brokers = new Set<string>();
    for (const f of m.fees) {
      if (!S.has(f.account)) continue;
      const o = (feeByYear[f.year] ||= { comm: 0, other: 0, byBroker: {} });
      if (f.kind === "prowizja") o.comm += f.v; else o.other += f.v;
      const b = m.acc[f.account].broker;
      brokers.add(b);
      o.byBroker[b] = (o.byBroker[b] || 0) + f.v;
    }
    return { feeByYear, brokers: [...brokers].sort() };
  });
  const feeByYear = $derived(fees.feeByYear);
  const years = $derived([...new Set([...Object.keys(feeByYear), ...Object.keys(tc.byYear)].map(String))].sort().reverse());
  const sum = (f: (o: FeeYear) => number) => Object.values(feeByYear).reduce((s, o) => s + f(o), 0);
  const terYear = $derived(tc.rows.reduce((s, r) => s + r.annual, 0));
  const terPaid = $derived(tc.rows.reduce((s, r) => s + r.paid, 0));
  const fundVal = $derived(tc.rows.reduce((s, r) => s + r.value, 0));
  const A = $derived(need(app.agg));
  const e = $derived(m.idx(m.today));
  const yNow = $derived(m.today.slice(0, 4));
  const lastY = $derived(feeByYear[yNow] || { comm: 0, other: 0 });
  const terRows = $derived(tc.rows.filter((r) => r.value > 1 || r.paid > 1).sort((a, b) => b.annual - a.annual));
  const feeRows = $derived(Object.entries(feeByYear).sort().reverse());

  // after saving, fields are re-rendered from the saved values (as after redrawing the view)
  let gen = $state(0);
  let terTable: HTMLDivElement | undefined = $state();
  async function saveTer() {
    const t: Record<string, number> = { ...terMap };
    terTable?.querySelectorAll<HTMLInputElement>(".terIn").forEach((x) => { if (x.value !== "") t[x.dataset.k as string] = parseNum(x.value, 0); });
    await saveConfig({ ...cfg, ter: t });
    toast("Zapisano TER");
    gen++;
  }

  const costOpt = $derived.by(() => {
    const p = palette();
    const ys = years.slice().reverse();
    return {
      ...baseOption(p), tooltip: { ...baseOption(p).tooltip, axisPointer: { type: "shadow" }, formatter: (ps: TipParam) => tooltipRows(ps, (v) => tipMoney(v, 2)) },
      xAxis: { type: "category", data: ys, axisTick: { show: false }, axisLine: { lineStyle: { color: p.axis } }, axisLabel: { color: p.muted } },
      yAxis: valueAxis(compactMoney, p),
      series: [
        { name: "Prowizje", type: "bar", stack: "k", barMaxWidth: 34, data: ys.map((y) => +(feeByYear[y]?.comm || 0).toFixed(2)), itemStyle: { color: series(0) } },
        { name: "Inne opłaty", type: "bar", stack: "k", data: ys.map((y) => +(feeByYear[y]?.other || 0).toFixed(2)), itemStyle: { color: series(2) } },
        { name: "TER funduszy (szac.)", type: "bar", stack: "k", data: ys.map((y) => +(tc.byYear[y] || 0).toFixed(2)), itemStyle: { color: series(1) } },
      ],
    };
  });
</script>

<ViewHead title="Koszty" sub="prowizje maklerskie, opłaty i ukryty koszt funduszy (TER)" />
<div class="kpis">
  <Kpi label="Prowizje łącznie">{#snippet value()}<Money v={sum((o) => o.comm)} d={2} />{/snippet}{#snippet sub()}w {yNow}: <Money v={lastY.comm} d={2} />{/snippet}</Kpi>
  <Kpi label="Inne opłaty" sub="opłaty brokerów, za przelewy itp.">{#snippet value()}<Money v={sum((o) => o.other)} d={2} />{/snippet}</Kpi>
  <Kpi label="TER: koszt roczny teraz">{#snippet value()}<Money v={terYear} />{/snippet}{#snippet sub()}<Pct v={fundVal > 0 ? terYear / fundVal : null} d={2} sign={false} /> średnio od <Money v={fundVal} /> w ETF-ach{/snippet}</Kpi>
  <Kpi label="TER: zapłacone dotąd (szac.)" sub="pobierane codziennie z wartości funduszu — niewidoczne w historii">{#snippet value()}<Money v={terPaid} />{/snippet}</Kpi>
  <Kpi label="Wszystkie koszty rocznie">{#snippet value()}<Money v={terYear + lastY.comm + lastY.other} />{/snippet}{#snippet sub()}<Pct v={A.V[e] > 0 ? (terYear + lastY.comm + lastY.other) / A.V[e] : null} d={2} sign={false} /> wartości portfela (TER + koszty z {yNow}){/snippet}</Kpi>
</div>
<Card title="Koszty rok po roku" hint="TER szacowany z dziennej wartości funduszy i stawki TER"><Chart id="chCost" option={costOpt} /></Card>
<Card title="TER funduszy">
  <div class="table-wrap" bind:this={terTable}><table><thead><tr><th class="l">Fundusz</th><th>Wartość</th><th>TER %</th><th>Rocznie</th><th>Zapłacone dotąd</th></tr></thead><tbody>
    {#each terRows as r, i (i)}
      <tr><td class="l">{r.name} <span class="muted small">{r.isin}</span></td><td><Money v={r.value} /></td>
        <td>{#key gen}<input type="number" class="num terIn" data-k={r.isin || r.key} value={r.ter ?? ""} step="0.01" min="0" style="width:70px" />{/key}</td><td><Money v={r.annual} /></td><td><Money v={r.paid} /></td></tr>
    {/each}
  </tbody></table></div><div class="row" style="margin-top:10px"><button class="btn primary" id="saveTer" onclick={saveTer}>Zapisz TER</button><span class="small muted">domyślne wartości z dokumentów KID emitentów — sprawdź przy zmianach</span></div>
</Card>
<Card title="Prowizje i opłaty wg brokerów">
  <div class="table-wrap"><table><thead><tr><th class="l">Rok</th>{#each fees.brokers as b (b)}<th>{b}</th>{/each}<th>Razem</th></tr></thead><tbody>
    {#each feeRows as [y, o] (y)}
      <tr><td class="l"><b>{y}</b></td>{#each fees.brokers as b (b)}<td>{#if o.byBroker[b]}<Money v={o.byBroker[b]} d={2} />{:else}–{/if}</td>{/each}<td><b><Money v={o.comm + o.other} d={2} /></b></td></tr>
    {:else}
      <tr><td class="l muted">Brak kosztów.</td></tr>
    {/each}
  </tbody></table></div>
</Card>
